import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'
import type { ReactNode } from 'react'
import {
  Button as AriaButton,
  FieldError,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
  Switch,
  Text,
  TextArea,
  TextField,
} from 'react-aria-components'

import { useAdminUiLabels } from '../i18n'

// Compact admin form controls on React Aria, styled with the --mv-* tokens
// (medverse admin density: text-sm inputs, 12px labels, 11px hints).

const LABEL = 'text-xs font-medium text-[var(--fg-muted)]'
const HINT = 'text-[11px] text-[var(--fg-quiet)]'
const ERROR = 'text-xs text-[var(--color-danger)]'
export const INPUT =
  'w-full rounded-[var(--mv-radius-xs)] border border-[var(--mv-border)] bg-[var(--mv-surface)] px-2.5 py-2 text-sm text-[var(--mv-fg-strong)] outline-none transition-[border-color,box-shadow] placeholder:text-[var(--mv-fg-subtle)] data-[focused]:border-[var(--mv-blue)] data-[focused]:shadow-[var(--mv-ring)] data-[invalid]:border-[var(--color-danger)] data-[disabled]:opacity-60'

type BaseProps = {
  label: string
  value: string
  onChange: (v: string) => void
  hint?: ReactNode | undefined
  error?: string | undefined
  placeholder?: string | undefined
  isRequired?: boolean | undefined
  isDisabled?: boolean | undefined
  className?: string | undefined
  /** Visible-character counter, red when over — medverse SEO/rich-text counter style. */
  maxLength?: number | undefined
  softMax?: number | undefined
}

function Counter({ value, max }: { value: string; max: number }) {
  return (
    <span className={clsx('tabular-nums', value.length > max ? 'font-semibold text-[var(--color-danger)]' : '')}>
      {value.length} / {max}
    </span>
  )
}

export function Field({ label, value, onChange, hint, error, placeholder, isRequired, isDisabled, className, maxLength, softMax, type = 'text' }: BaseProps & { type?: 'text' | 'email' | 'password' | 'url' | 'number' | 'time' }) {
  return (
    <TextField
      value={value}
      onChange={onChange}
      isInvalid={!!error}
      {...(isRequired ? { isRequired } : {})}
      {...(isDisabled ? { isDisabled } : {})}
      {...(maxLength ? { maxLength } : {})}
      type={type}
      className={clsx('flex flex-col gap-1', className)}
    >
      <Label className={LABEL}>
        {label}
        {isRequired && <span className="text-[var(--color-danger)]"> *</span>}
      </Label>
      <Input className={INPUT} {...(placeholder ? { placeholder } : {})} />
      <div className="flex justify-between gap-2">
        {error ? <FieldError className={ERROR}>{error}</FieldError> : hint ? <Text slot="description" className={HINT}>{hint}</Text> : <span />}
        {softMax ? <span className={HINT}><Counter value={value} max={softMax} /></span> : null}
      </div>
    </TextField>
  )
}

export function TextAreaField({ label, value, onChange, hint, error, placeholder, isRequired, className, softMax, rows = 3 }: BaseProps & { rows?: number }) {
  return (
    <TextField value={value} onChange={onChange} isInvalid={!!error} {...(isRequired ? { isRequired } : {})} className={clsx('flex flex-col gap-1', className)}>
      <Label className={LABEL}>
        {label}
        {isRequired && <span className="text-[var(--color-danger)]"> *</span>}
      </Label>
      <TextArea rows={rows} className={clsx(INPUT, 'resize-y leading-relaxed')} {...(placeholder ? { placeholder } : {})} />
      <div className="flex justify-between gap-2">
        {error ? <FieldError className={ERROR}>{error}</FieldError> : hint ? <Text slot="description" className={HINT}>{hint}</Text> : <span />}
        {softMax ? <span className={HINT}><Counter value={value} max={softMax} /></span> : null}
      </div>
    </TextField>
  )
}

export type SelectOption<T extends string> = { value: T; label: string }

export function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
  hint,
  className,
  placeholder,
  isDisabled,
  hideLabel,
}: {
  label: string
  value: T | null
  onChange: (v: T) => void
  options: readonly SelectOption<T>[]
  hint?: string | undefined
  className?: string | undefined
  placeholder?: string | undefined
  isDisabled?: boolean | undefined
  /** Keep the label for screen readers only (a select inside a table row or a compact form line). */
  hideLabel?: boolean | undefined
}) {
  const labels = useAdminUiLabels()
  return (
    <Select
      selectedKey={value}
      onSelectionChange={(k) => k !== null && onChange(k as T)}
      placeholder={placeholder ?? labels.select}
      {...(isDisabled ? { isDisabled } : {})}
      className={clsx('flex flex-col gap-1', className)}
    >
      <Label className={clsx(LABEL, hideLabel && 'sr-only')}>{label}</Label>
      <AriaButton className={clsx(INPUT, 'flex items-center justify-between text-left data-[focus-visible]:border-[var(--mv-blue)] data-[focus-visible]:shadow-[var(--mv-ring)]')}>
        <SelectValue className="truncate data-[placeholder]:text-[var(--mv-fg-subtle)]" />
        <ChevronDown size={16} className="shrink-0 text-[var(--mv-fg-subtle)]" />
      </AriaButton>
      {hint && <Text slot="description" className={HINT}>{hint}</Text>}
      <Popover className="max-h-72 w-[--trigger-width] overflow-auto rounded-[var(--mv-radius-sm)] border border-[var(--mv-border)] bg-[var(--mv-surface-overlay)] p-1 shadow-[var(--mv-shadow-md)]">
        <ListBox className="outline-none">
          {options.map((o) => (
            <ListBoxItem
              key={o.value}
              id={o.value}
              textValue={o.label}
              className="cursor-pointer rounded-[var(--mv-radius-xs)] px-2.5 py-1.5 text-sm text-[var(--mv-fg)] outline-none data-[focused]:bg-[var(--mv-grey-100)] data-[selected]:font-semibold data-[selected]:text-[var(--mv-blue)]"
            >
              {o.label}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  )
}

export function SwitchField({ label, isSelected, onChange, hint, isDisabled }: { label: string; isSelected: boolean; onChange: (v: boolean) => void; hint?: string; isDisabled?: boolean | undefined }) {
  return (
    <div className="flex flex-col gap-1">
      <Switch isSelected={isSelected} onChange={onChange} {...(isDisabled ? { isDisabled } : {})} className="group flex cursor-pointer data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60 items-center gap-2.5 text-sm text-[var(--mv-fg)]">
        <span className="flex h-5 w-9 shrink-0 items-center rounded-full bg-[var(--mv-border-strong)] p-0.5 transition-colors group-data-[selected]:bg-[var(--mv-blue)] group-data-[focus-visible]:shadow-[var(--mv-ring)]">
          <span className="h-4 w-4 rounded-full bg-white shadow-[var(--mv-shadow-xs)] transition-transform group-data-[selected]:translate-x-4" />
        </span>
        {label}
      </Switch>
      {hint && <span className={HINT}>{hint}</span>}
    </div>
  )
}

/** Red `role="alert"` band for API errors, medverse style. */
export function ErrorBand({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="rounded-[var(--mv-radius-sm)] border border-[rgba(214,69,69,0.25)] bg-[rgba(214,69,69,0.06)] px-3 py-2 text-sm text-[var(--color-danger)]">
      {children}
    </div>
  )
}

/** Small outline action button used in table rows and panel headers. */
export function SmallButton({ children, onPress, tone = 'default', isDisabled, type = 'button' }: { children: ReactNode; onPress?: () => void; tone?: 'default' | 'danger' | 'primary'; isDisabled?: boolean; type?: 'button' | 'submit' }) {
  return (
    <AriaButton
      type={type}
      {...(onPress ? { onPress } : {})}
      {...(isDisabled ? { isDisabled } : {})}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-[var(--mv-radius-xs)] border px-3 py-1.5 text-xs font-medium transition-colors data-[disabled]:opacity-50',
        tone === 'danger' && 'border-[rgba(214,69,69,0.35)] text-[var(--color-danger)] hover:bg-[rgba(214,69,69,0.06)]',
        tone === 'primary' && 'border-[var(--mv-blue)] bg-[var(--mv-blue)] text-white hover:bg-primary-600',
        tone === 'default' && 'border-[var(--mv-border)] text-[var(--mv-fg)] hover:bg-[var(--mv-grey-100)]',
      )}
    >
      {children}
    </AriaButton>
  )
}
